/**
 * Animated counter module.
 *
 * Finds every `.counter-item` element whose text contains a number,
 * parses the value (supports suffixes like k, M and trailing symbols like +),
 * and counts up from 0 when the element scrolls into view.
 *
 * Supported formats: "42k+", "1.2M", "200+", "99", "$500", "3.5B"
 *
 * Usage: import and call initCounters() from main.js.
 */

const DURATION = 1800; // ms
const EASING   = t => 1 - Math.pow( 1 - t, 3 ); // ease-out cubic

// ─── Value parsing ────────────────────────────────────────────────────────────

const MULTIPLIERS = { k: 1_000, m: 1_000_000, b: 1_000_000_000 };

/**
 * Pull the numeric value, suffix multiplier, decimal places, prefix, and
 * trailing symbol out of a raw string such as "1.2M" or "$42k+".
 *
 * @param {string} raw
 * @returns {{ numeric: number, decimals: number, prefix: string, suffix: string, trail: string } | null}
 */
function parse( raw ) {
    // Match: optional prefix chars, a number (int or decimal), optional k/M/B, optional trailing chars
    const match = raw.match( /^([^0-9]*)(\d+(?:\.\d+)?)([kKmMbB]?)([^0-9]*)$/ );
    if ( ! match ) return null;

    const [ , prefix, numStr, magnitude, trail ] = match;
    const multiplier = MULTIPLIERS[ magnitude.toLowerCase() ] ?? 1;
    const decimals   = numStr.includes( '.' ) ? numStr.split( '.' )[ 1 ].length : 0;

    return {
        numeric:  parseFloat( numStr ) * multiplier,
        decimals,
        prefix,
        suffix:   magnitude.toUpperCase(),
        trail,
    };
}

/**
 * Format a raw number back into the display string, re-applying the suffix
 * multiplier so "42000" renders as "42k" not "42000".
 *
 * @param {number} value      Current animated value (full integer/decimal).
 * @param {object} meta       Result of parse().
 * @returns {string}
 */
function format( value, meta ) {
    const multiplier = MULTIPLIERS[ meta.suffix.toLowerCase() ] ?? 1;
    const display    = ( value / multiplier ).toFixed( meta.decimals );
    return meta.prefix + display + meta.suffix + meta.trail;
}

// ─── Animation ────────────────────────────────────────────────────────────────

/**
 * Animate a single element from 0 to its target value.
 *
 * @param {HTMLElement} el
 * @param {object}      meta  Result of parse().
 */
function animateCounter( el, meta ) {
    const start = performance.now();

    function step( now ) {
        const elapsed  = now - start;
        const progress = Math.min( elapsed / DURATION, 1 );
        const eased    = EASING( progress );

        el.textContent = format( meta.numeric * eased, meta );

        if ( progress < 1 ) {
            requestAnimationFrame( step );
        } else {
            // Snap to the exact final value to avoid floating-point drift.
            el.textContent = format( meta.numeric, meta );
        }
    }

    requestAnimationFrame( step );
}

// ─── Initialisation ───────────────────────────────────────────────────────────

/**
 * Scan for `.counter-item` elements containing a parseable number, then
 * trigger their count-up animation when they enter the viewport.
 *
 * Safe to call after DOMContentLoaded (script is footer-loaded).
 */
function initCounters() {
    const candidates = document.querySelectorAll( '.counter-item' );
    if ( ! candidates.length ) return;

    const targets = [];

    candidates.forEach( el => {
        const meta = parse( el.textContent.trim() );
        if ( ! meta ) return;

        // Store the parsed metadata and freeze the original text as a data
        // attribute so we can restore it if needed.
        el.dataset.counterTarget = el.textContent.trim();
        targets.push( { el, meta } );
    } );

    if ( ! targets.length ) return;

    const observed = new Set();

    const io = new IntersectionObserver(
        entries => {
            entries.forEach( entry => {
                if ( ! entry.isIntersecting ) return;
                if ( observed.has( entry.target ) ) return;

                observed.add( entry.target );
                io.unobserve( entry.target );

                const target = targets.find( t => t.el === entry.target );
                if ( target ) animateCounter( target.el, target.meta );
            } );
        },
        { threshold: 0.25 }
    );

    targets.forEach( ( { el } ) => io.observe( el ) );
}

export default initCounters;
